import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon } from 'lucide-react';
interface CustomPlatformDropdownProps {
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: string[];
  placeholder?: string;
  error?: boolean;
  className?: string;
}
export const CustomPlatformDropdown: React.FC<CustomPlatformDropdownProps> = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder = 'Select a platform',
  error = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Enable scrolling inside dropdown without affecting page scroll
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (isOpen && optionsRef.current && optionsRef.current.contains(e.target as Node)) {
        e.stopPropagation();
        // Don't prevent default - this allows natural scrolling within the dropdown
      }
    };
    // Options container should handle its own scrolling
    if (optionsRef.current) {
      optionsRef.current.addEventListener('wheel', handleWheel, {
        passive: false
      });
    }
    return () => {
      if (optionsRef.current) {
        optionsRef.current.removeEventListener('wheel', handleWheel);
      }
    };
  }, [isOpen, optionsRef.current]);
  const handleSelect = (option: string) => {
    // Create a synthetic event that mimics a native select change event
    const syntheticEvent = {
      target: {
        name,
        value: option
      }
    } as React.ChangeEvent<HTMLSelectElement>;
    onChange(syntheticEvent);
    setIsOpen(false);
    setSearchTerm('');
  };
  const filteredOptions = searchTerm ? options.filter(option => option.toLowerCase().includes(searchTerm.toLowerCase())) : options;
  // Handle toggle without affecting page scroll
  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };
  return <div className={`relative w-full ${className}`} ref={dropdownRef} data-id="element-720">
      {/* Hidden native select for form compatibility */}
      <select id={id} name={name} value={value} onChange={onChange} className="sr-only" aria-hidden="true" data-id="element-721">
        <option value="" data-id="element-722">{placeholder}</option>
        {options.map(option => <option key={option} value={option} data-id="element-723">
            {option}
          </option>)}
      </select>
      {/* Custom dropdown trigger */}
      <div className={`w-full px-4 py-2 rounded-lg border ${error ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent bg-white dark:bg-[#0F1F1D] text-gray-800 dark:text-[#B0C1BD] cursor-pointer flex items-center justify-between`} onClick={toggleDropdown} tabIndex={0} role="combobox" aria-expanded={isOpen} aria-haspopup="listbox" aria-controls={`${id}-options`} data-id="element-724">
        <span className={!value ? 'text-gray-500 dark:text-[#7A8F8A]' : ''} data-id="element-725">
          {value || placeholder}
        </span>
        <ChevronDownIcon className={`h-5 w-5 text-gray-500 dark:text-[#7A8F8A] transition-transform ${isOpen ? 'transform rotate-180' : ''}`} data-id="element-726" />
      </div>
      {/* Dropdown options */}
      {isOpen && <div ref={optionsRef} className="absolute z-50 w-full mt-1 bg-white dark:bg-[#0F1F1D] border border-gray-300 dark:border-[#1A3330] rounded-lg shadow-lg max-h-60 overflow-y-auto" id={`${id}-options`} role="listbox" data-id="element-727">
          {/* Search input */}
          <div className="sticky top-0 p-2 bg-white dark:bg-[#0F1F1D] border-b border-gray-200 dark:border-[#1A3330]" data-id="element-728">
            <input type="text" className="w-full p-2 text-sm border border-gray-300 dark:border-[#1A3330] rounded-md bg-white dark:bg-[#0F1F1D] text-gray-800 dark:text-[#B0C1BD] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]" placeholder="Search platforms..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onClick={e => e.stopPropagation()} data-id="element-729" />
          </div>
          {/* Options */}
          <div data-id="element-730">
            {filteredOptions.length > 0 ? filteredOptions.map(option => <div key={option} className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1A3330] cursor-pointer text-gray-800 dark:text-[#B0C1BD]" role="option" aria-selected={value === option} onClick={() => handleSelect(option)} data-id="element-731">
                  {option}
                </div>) : <div className="px-4 py-3 text-gray-500 dark:text-[#7A8F8A] text-center" data-id="element-732">
                No platforms match your search
              </div>}
          </div>
        </div>}
    </div>;
};